import Swal from "sweetalert2/dist/sweetalert2.js"

const entityDeletionModal = (entityName, testField) => ({
  iconHtml: `
    <div class="icon-box icon-box--warning">
      <i class="icon-delete text-2xl text-redpink text-32"></i>
    </div>
  `,
  title: `Delete ${entityName}`,
  html: `
    <p class="text-center">Are you sure you want to delete ${entityName}?</p>
    <p class="text-center">This action cannot be undone.</p>
    <p class="text-center">You must enter the <span class="font-bold">${testField}</span> in the box below to confirm your action.</p>
  `,
  showCancelButton: true,
  cancelButtonText: "Cancel",
  confirmButtonText: "Delete"
})

const streamDeletionModal = () => ({
  iconHtml: `
    <div class="icon-box icon-box--warning">
      <i class="icon-delete text-2xl text-redpink text-32"></i>
    </div>
  `,
  title: "Delete Stream",
  html: `
    <p class="text-center">Are you sure you want to delete this stream?</p>
    <p class="text-center">This permanently deletes the stream and all of its associated data, including recordings, participants, speakers, and chats. This action cannot be undone.</p>
  `,
  showCancelButton: true,
  cancelButtonText: "Cancel",
  confirmButtonText: "Delete"
})

const archiveIdpStreamModal = () => ({
  iconHtml: `
    <div class="icon-box icon-box--warning">
      <i class="icon-view-off text-2xl text-redpink text-32"></i>
    </div>
  `,
  title: "Archive Stream",
  html: `
    <p class="text-center">This stream is published to IDP and can't be deleted while it's synced.</p>
    <p class="text-center">Archiving hides it from viewers while keeping its data intact.</p>
  `,
  showCancelButton: true,
  cancelButtonText: "Cancel",
  confirmButtonText: "Archive"
})

const updateLandingPageLinkModal = () => ({
  iconHtml: `
    <div class="icon-box icon-box--warning">
      <i class="icon-features text-2xl text-redpink text-32"></i>
    </div>
  `,
  title: "Update link?",
  html: `
    <p class="text-center">Your changes will generate a new link.</p>
    <p class="text-center">Anyone using the current link will continue to see the original version and will not receive these updates. To share the updated version, you'll need to use the new link.</p>
    <p class="text-center">Do you want to continue?</p>
  `,
  showCancelButton: true,
  cancelButtonText: "Cancel",
  confirmButtonText: "Continue"
})

const resetPasswordModal = () => {
  return {
    iconHtml: `
      <div class="icon-box icon-box--warning">
        <i class="icon-password text-2xl text-redpink text-32"></i>
      </div>
    `,
    title: `Reset Password`,
    html: `
      <p class="text-center">Are you sure you want to reset password for this user?</p>
      <p class="text-center">New password will be sent to the user by email.</p>
      <p class="text-center">This action cannot be undone.</p>
    `,
    showCancelButton: true,
    cancelButtonText: "Cancel",
    confirmButtonText: "Reset Password"
  }
}

const updateFeatureFlagModal = newState => {
  const humanizedState = newState ? "Enable" : "Disable"
  const iconBoxClass = newState ? "icon-box--success" : "icon-box--warning"
  const iconTextClass = newState ? "text-green" : "text-redpink"

  return {
    iconHtml: `
      <div class="icon-box ${iconBoxClass}">
        <i class="icon-features text-2xl ${iconTextClass} text-32"></i>
      </div>
    `,
    title: `${humanizedState} Feature Flag`,
    html: `
      <p class="text-center">Are you sure you want to ${humanizedState} this feature flag?</p>
    `,
    showCancelButton: true,
    cancelButtonText: "Cancel",
    confirmButtonText: humanizedState
  }
}

const simpleActionConfirmModal = ({ title, text = null, confirmButtonText = "Confirm" }) => {
  const options = {
    title,
    html: `
      <p class="text-center">${text}</p>
    `,
    showCancelButton: true,
    cancelButtonText: "Cancel",
    confirmButtonText
  }

  if (!text) {
    delete options.html
  }

  return options
}

const defaultModal = text => {
  return {
    iconHtml: `
      <div class="icon-box icon-box--warning">
        <i class="fa fa-warning text-2xl text-redpink text-32"></i>
      </div>
    `,
    title: text,
    showCancelButton: true,
    cancelButtonText: "Cancel",
    confirmButtonText: "Yes"
  }
}

const uploaderModal = () => {
  return {
    title: "Upload Files",
    cancelButtonText: "Cancel",
    confirmButtonText: "Send"
  }
}

const modalOptions = {
  "delete-user": () => entityDeletionModal("User", "User Email"),
  "delete-university": () => entityDeletionModal("Organization", "Organization Name"),
  "delete-university-group": () => entityDeletionModal("University Group", "Group Name"),
  "delete-organization-tag": () => entityDeletionModal("Organization Tag", "Tag Name"),
  "delete-stream": () => streamDeletionModal(),
  "archive-idp-stream": () => archiveIdpStreamModal(),
  "confirm-landing-page-update": () => updateLandingPageLinkModal(),
  "reset-password": () => resetPasswordModal(),
  "enable-feature-flag": () => updateFeatureFlagModal(true),
  "disable-feature-flag": () => updateFeatureFlagModal(false),
  "confirm-users-time-limits": data => simpleActionConfirmModal(data),
  "confirm-users-permissions": data => simpleActionConfirmModal(data),
  "confirm-delete-users": data => simpleActionConfirmModal(data),
  "confirm-publish-contents": data => simpleActionConfirmModal(data),
  "confirm-unpublish-contents": data => simpleActionConfirmModal(data),
  "confirm-download-contents": data => simpleActionConfirmModal(data),
  "confirm-archive-contents": data => simpleActionConfirmModal(data),
  "confirm-unarchive-contents": data => simpleActionConfirmModal(data),
  "confirm-delete-contents": data => simpleActionConfirmModal(data),
  "confirm-delete-space": data => simpleActionConfirmModal(data),
  "confirm-delete-space-user": data => simpleActionConfirmModal(data),
  "confirm-block-prospect": data => simpleActionConfirmModal(data),
  "confirm-time-report-save": () => simpleActionConfirmModal({
    title: "Save Time Entry",
    text: "Are you sure you want to save these changes to the activity record?",
    confirmButtonText: "Save"
  }),
  "confirm-time-report-delete": () => simpleActionConfirmModal({
    title: "Delete Time Entry",
    text: "Are you sure? This action cannot be undone. This will permanently delete this activity record.",
    confirmButtonText: "Delete"
  }),
  "default": text => defaultModal(text),
  "default-confirm": data => simpleActionConfirmModal(data),
  "uploader": data => uploaderModal(data),
  "unsaved-changes": () => simpleActionConfirmModal({
    title: "Unsaved Changes",
    text: "Are you sure you want to leave the page without saving changes?"
  })
}

const customClasses = {
  container: "swal-modal-container",
  popup: "swal-modal-popup",
  title: "swal-modal-title",
  icon: "swal-modal-icon",
  htmlContainer: "swal-modal-html-container",
  actions: "swal-modal-actions",
  confirmButton: "swal-confirm-btn",
  cancelButton: "swal-cancel-btn",
  loader: "hidden"
}

export const openDeletionProgressModal = (universityId) => {
  const trackedId = String(universityId)

  const updatePercentage = (event) => {
    if (String(event.detail.universityId) !== trackedId) return

    const percentageElement = Swal.getPopup()?.querySelector("[data-deletion-percentage]")
    if (percentageElement) percentageElement.textContent = `${event.detail.percentage}%`
  }

  const closeWhenFinished = (event) => {
    if (String(event.detail.universityId) !== trackedId) return

    Swal.close()
  }

  const removeProgressListeners = () => {
    window.removeEventListener("university:deletion:progress", updatePercentage)
    window.removeEventListener("university:deletion:finished", closeWhenFinished)
  }

  window.addEventListener("university:deletion:progress", updatePercentage)
  window.addEventListener("university:deletion:finished", closeWhenFinished)

  Swal.fire({
    title: "Deleting university…",
    html: `
      <div class="flex flex-col items-center gap-16">
        <div class="spinner spinner--lg"></div>
        <p class="text-3xl font-semibold text-gray-900" data-deletion-percentage>0%</p>
        <p class="text-center text-sm text-gray-500">The university deletion process has started and is running in the background. You will receive an email notification when it is complete.</p>
      </div>
    `,
    showConfirmButton: true,
    confirmButtonText: "Run in background",
    allowOutsideClick: true,
    allowEscapeKey: true,
    customClass: customClasses
  }).then(removeProgressListeners)
}

export const requestConfirm = async (confirmData) => {
  try {
    const modal = confirmData.modal || "default"

    let options
    if (modal === "default") {
      options = modalOptions.default(confirmData.text)
    } else {
      options = modalOptions[modal](confirmData.data)
    }

    if (!options) {
      console.error(`No options defined for modal: ${modal}`)
      return false
    }

    if (confirmData.test) {
      const normalize = string => string.replace(/\s+/g, " ").trim()
      const expected = normalize(confirmData.test)

      options.input = "text"
      options.inputLabel = confirmData.test_label
      options.inputValidator = value => {
        if (normalize(value) !== expected) {
          return confirmData.validationMessage || "incorrect"
        }
      }
    }

    const userAction = await Swal.fire({
      ...options,
      customClass: customClasses
    })

    if (userAction.isConfirmed && confirmData.form) {
      const form = document.querySelector(confirmData.form)
      if (form) {
        form.submit()
      }
    }

    return userAction.isConfirmed
  } catch (error) {
    console.error("Error processing confirm modal:", error)
    return false
  }
}

const customTurboConfirm = async (confirmData) => {
  const options = JSON.parse(confirmData)

  return requestConfirm(options)
}

window.confirm = customTurboConfirm
